// Host reporting for Turkey Hunt. Uses the two endpoints every 3D course
// already uses (see ui/Debrief.tsx): the quiz goes to /t/<token>/quiz (graded
// by grade_and_complete like any e-learning) and the per-question detail goes
// to /sim/track as a TrainingEvent. No new endpoints.

import { apiUrl, getHostConfig, isSpokeLoad } from "../../lib/hostConfig";
import type { QuizQuestion } from "../../scenario/types";

export type AnswerResult = "correct" | "incorrect" | "unanswered";

export interface AnswerRecord {
  questionId: string;
  position: number;
  topic: string;
  selected: string | null;
  result: AnswerResult;
  responseMs: number;
  attempts: number;
  missedShots: number;
  points: number;
}

export interface SessionSummary {
  sessionId: string;
  mode: "learning" | "arcade";
  finalScore: number;
  correct: number;
  total: number;
  shotAccuracy: number;
  maxCombo: number;
  totalTimeMs: number;
  stars: number;
  completed: boolean;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

export async function reportSession(
  beatId: string,
  source: QuizQuestion[],
  records: AnswerRecord[],
  summary: SessionSummary,
): Promise<void> {
  const host = getHostConfig();
  if (!host.hosted || !host.token) return;
  if (isSpokeLoad()) return;
  const csrf = readCookie("cf_csrf");
  const byId = new Map(records.map((r) => [r.questionId, r]));

  if (source.length && csrf) {
    const submissions = source.map((q, i) => {
      const rec = byId.get(q.id);
      return { question_position: i, selected_choices: rec?.selected ? [rec.selected] : [] };
    });
    try {
      await fetch(apiUrl(`/t/${host.token}/quiz`), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
        credentials: "include",
        body: JSON.stringify({ submissions }),
      });
    } catch {
      /* best-effort */
    }
  }

  try {
    await fetch(apiUrl("/sim/track"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
      credentials: "include",
      body: JSON.stringify({
        token: host.token,
        kind: "sim_module",
        module_id: "turkey_hunt",
        event: "game_completed",
        score: {
          session_id: summary.sessionId,
          mode: summary.mode,
          final_score: summary.finalScore,
          correct: summary.correct,
          total: summary.total,
          shot_accuracy: summary.shotAccuracy,
          max_combo: summary.maxCombo,
          total_time_ms: summary.totalTimeMs,
          stars: summary.stars,
          completed: summary.completed,
        },
        outcomes: records.map((r) => ({
          type: "quiz_answered",
          beatId,
          eventId: r.questionId,
          evidence: r.result,
          selected: r.selected,
          responseMs: r.responseMs,
          attempts: r.attempts,
          missedShots: r.missedShots,
          points: r.points,
          at: Date.now(),
        })),
      }),
    });
  } catch {
    /* best-effort */
  }
}
